var Control = require("enyo/Control"),
    kind    = require("enyo/kind");

require("enyo-ui");

/**
 * [ScrollMap]{@link enyo-ui.ScrollMap} is a control that allows the use of
 * `ontap` events to make a [Scroller]{@link enyo-ui.Scroller} scroll to a
 * particular coordinates.
 *
 * The coordinates of the tap within the bounds of the [ScrollMap]{@link
 * enyo-ui.ScrollMap} are mapped proportionately to coordinates in the
 * scrollers' content. The scroller is then instructed to scroll to these
 * coordinates.
 *
 * @ui
 * @class   enyo-ui.ScrollMap
 * @extends enyo.Control
 * @public
 */
module.exports = kind(/** @lends enyo-ui.ScrollMap.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollMap",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    classes: "enyo-ui-scroll-map",

    /**
     * The scroller we're currently working with.
     *
     * @type    {?enyo-ui.Scroller}
     * @default null
     * @private
     */
    scroller: null,

    /**
     * Set scroll axis to "vertical", "horizontal", "both" or `null`.
     *
     * @type    {?string}
     * @default null
     * @public
     */
    axis: null,

    /**
     * Flag whether a scroll should be initiated on tap.
     *
     * @type    {boolean}
     * @default true
     * @public
     */
    shouldScrollOnTap: true,

    /**
     * @private
     */
    handlers: {
        ontap: "onTap"
    },

    /**
     * Set a css class to match the scroll axis.
     *
     * @param {string}  oldAxis  The old axis
     * @param {string}  newAxis  The new axis
     * @private
     */
    axisChanged: function(oldAxis, newAxis) {
        switch (oldAxis) {
            case "vertical":
            case "horizontal":
            case "both":
                this.removeClass("enyo-ui-scroll-map--scroll-axis-" + oldAxis);
                break;
            case null:
                this.removeClass("enyo-ui-scroll-map--scroll-axis-none");
                break;
            default:
                break;
        }

        switch (newAxis) {
            case "vertical":
            case "horizontal":
            case "both":
                this.addClass("enyo-ui-scroll-map--scroll-axis-" + newAxis);
                break;
            case null:
                this.addClass("enyo-ui-scroll-map--scroll-axis-none");
                break;
            default:
                this.error("Invalid scroll axis", newAxis);
                break;
        }
    },

    /**
     * Makes the scroller scroll on tap.
     *
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object
     * @private
     */
    onTap: function(sender, event) {
        if (this.shouldScrollOnTap) {
            this.scrollOnTap(event);
        }
    },

    /**
     * Proportionately maps coordinates of the tap within the bounds this
     * control coordinates in the scrollers' content.
     *
     * @param {Event}  event  The tap event
     * @private
     */
    scrollOnTap: function(event) {
        var scroller = this.scroller,
            axis     = this.axis,
            scrollLimits,
            absoluteBounds,
            horizontalMultiplier,
            verticalMultiplier,
            scrollTargetTop,
            scrollTargetLeft;

        // No scroller
        if (!scroller || axis === null) {
            return;
        }

        // Get bounds
        scrollLimits   = scroller.scrollLimits;
        absoluteBounds = this.getAbsoluteBounds();

        // Calculate vertical scroll target
        if (axis === "vertical" || axis === "both") {
            verticalMultiplier = 1 / (absoluteBounds.height / (event.pageY - absoluteBounds.top));
            scrollTargetTop    = scrollLimits.maxTop * verticalMultiplier;
        }

        // Calculate horizontal scroll target
        if (axis === "horizontal" || axis === "both") {
            horizontalMultiplier = 1 / (absoluteBounds.width / (event.pageX - absoluteBounds.left));
            scrollTargetLeft     = scrollLimits.maxLeft * horizontalMultiplier;
        }

        // Apply scroll
        switch (axis) {
            case "vertical":
                scroller.scrollTo(undefined, scrollTargetTop);
                break;
            case "horizontal":
                scroller.scrollTo(scrollTargetLeft, undefined);
                break;
            case "both":
                scroller.scrollTo(scrollTargetLeft, scrollTargetTop);
                break;
            default:
                this.error("Invalid scroll axis", axis);
                break;
        }
    }

});
